<template>
  <div class="moin-message-wrapper" :class="[`source_${props.message.source}`]">
    <div class="knw_message_body">
      <div
        v-if="showHeader"
        class="moin-message-header"
        role="group"
        :aria-label="t('message.from', { name: message.operator.name })"
      >
        <Avatar :image="getAvatarUrl()" width="24px" height="24px" />
        <span>{{ message.operator.name }}</span>
      </div>
      <div class="moin-message-body">
        <RenderContent :message-data="message.data" />
      </div>
      <div v-if="isLastAiMessage && showAiIndicator" class="ai-hints">
        <!-- AI Generated, with sources -->
        <details v-if="gotAIRessources" ref="details" class="" @toggle="handleToggle">
          <summary class="ai-with-sources">
            <div class="expand">
              {{ message?.resourcesUsed?.length }} {{ t("message.ai.sources") }}
              <IconChevron
                :direction="isOpen ? 'down' : 'right'"
                style="flex-shrink: 0"
                width="20"
                height="20"
              />
            </div>
            <span class="ai-badge-container">
              {{ t("message.ai.generated") }}
              <IconSparkles width="16" height="16" />
            </span>
          </summary>

          <ol class="ai-sourcelist">
            <li
              v-for="source in message?.resourcesUsed"
              :key="source.source"
              :data-kind="source.type || 'URL'"
            >
              <a rel="noopener" target="_blank" :href="source.source">
                {{ sourceLabel(source) }}
              </a>
            </li>
          </ol>
        </details>
        <div v-else-if="isLastAiMessage && !gotAIRessources" class="ai-no-sources">
          {{ t("message.ai.generated") }}
          <IconSparkles width="16" height="16" aria-hidden="true" />
        </div>
      </div>
    </div>

    <!-- Outside white message bubble -->
    <p
      v-if="isLastAiMessage && showAiIndicator"
      class="moin-ai-disclaimer"
      :aria-description="t('message.ai.disclaimerDescription')"
    >
      {{ t("message.ai.disclaimer") }}
    </p>
  </div>
</template>

<script setup lang="ts">
import Avatar from "@/components/common/Avatar.vue";
import type { BotMessage } from "@/services/bot/messageBuilder";
import { computed, ref, useTemplateRef } from "vue";
import type { MessageProperties } from "@/components/widgetParts/chatPanel/chatConversation/ChatConversation.vue";
import RenderContent from "@/components/messages/RenderContent.vue";
import type { AiRessource } from "@/types/WSBotMessage";
import IconChevron from "@/svg/IconChevron.vue";
import IconSparkles from "@/svg/IconSparkles.vue";
import { useI18n } from "@/composables/useI18n";

const { t } = useI18n();

defineOptions({ name: "TextMessage" });

const props = defineProps<{
  message: BotMessage;
  showAiIndicator: boolean;
  messageProperties: MessageProperties;
}>();

const isOpen = ref(false);
const details = useTemplateRef("details");
function handleToggle() {
  isOpen.value = details?.value?.open || false;
}

const getAvatarUrl = () => props.message.operator.avatar_url;
const showHeader = computed(() => {
  if (props.message.source === "user") return false;
  if (props.messageProperties.isFirstMessage) return false;
  if (props.messageProperties.position === "start") return true;
  if (props.messageProperties.position === "single") return true;
  return false;
});

const isLastAiMessage = computed(() => {
  return props.messageProperties.isLastAiMessage;
});

const gotAIRessources = computed(() => {
  return props.message?.resourcesUsed && props.message?.resourcesUsed?.length > 0;
});

function sourceLabel(source: AiRessource) {
  if (source.type === "PDF") {
    if (source.pages) {
      return `PDF, ${t("message.ai.pages")} ${source.pages.join(", ")}`;
    }
    return "PDF";
  }
  return source.source;
}
</script>

<style lang="scss" scoped>
.moin-message-wrapper {
  // minimal distance to right side of the widget
  max-width: 80%;
  width: fit-content;

  &.source_bot,
  &.source_agent {
    justify-self: start;
  }

  &.source_user {
    justify-self: end;
  }

  &.source_user .knw_message_body {
    background-color: var(--kw-messageTypes-userMessage-background);
    color: var(--kw-messageTypes-userMessage-textcolor);
  }
}

.knw_message_body {
  justify-self: center;
  display: grid;
  grid-template-columns: 1fr;

  width: 100%;
  white-space: pre-line;
  padding: 12px;
  border-radius: var(--kw-widget-global-borderradius-outer);
  @include font_chat_text;
  background: var(--kw-messageTypes-textMessage-background);
  color: var(--kw-messageTypes-textMessage-textcolor, $kw-global-textcolor);
  border: 1px solid;
  border-color: var(--moin-message-bot-bordercolor, transparent);

  span {
    border-radius: 10px 0 10px 10px;
    background-color: var(--kw-message-user-background);
  }
}

.moin-message-body {
  padding: 10px 0;
  word-break: break-word;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }
}

.moin-message-header {
  display: flex;
  align-items: center;
  color: #707070;
  gap: 4px;
}

.ai-badge-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.ai-hints {
  color: #707070;

  .ai-no-sources {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .ai-with-sources {
    display: flex;
    justify-content: space-between;
    text-align: center;
    align-items: center;
    gap: 2rem;
    cursor: pointer;
    transition: color 0.2s ease-in-out;
    user-select: none;

    &:hover {
      color: var(--kw-messageTypes-textMessage-textcolor, $kw-global-textcolor);
      transition: color 0.2s ease-in-out;
    }

    .expand {
      display: flex;
      align-items: center;
    }
  }
}

.ai-sourcelist {
  list-style: decimal;
  padding: 8px 12px 8px calc(12px + 1rem);
  margin: 0;

  /* Break at will for URLs */
  word-break: break-all;
  [data-kind="PDF"] {
    /* Break normally for PDF page numbers */
    word-break: normal;
    overflow-wrap: break-word;
  }
}

.moin-ai-disclaimer {
  color: var(--moin-message-bot-subtextcolor, #6b6b6b);
  font-size: 1.25rem;
  margin-top: 1rem;
  text-align: center;
  width: 100%;
}
</style>
